import { PaymentCollectionType } from '@features/group-management/constants';
import {
  BulkPaymentDiscountType,
  FeeCategoryDataType,
  FeeConfigurationType,
  TermsConfig
} from '@types';

export type PaymentScheduleType = {
  start_date?: string;
  end_date?: string | null;
  interval?: string;
  interval_frequency?: number;
  interval_execution_type?: string;
  monthly_config?: Record<string, unknown>;
  weekly_config?: Record<string, unknown>;
  term_details?: unknown[];
  sessions_per_cycle?: number;
  custom_amount_minimum?: number | null;
  bulk_payment_discount_details?: BulkPaymentDiscountType[] | null;
};

export type GroupByIdResponse = {
  id: string;
  name: string;
  image_url?: string;
  amount: string;
  frequency: string;
  interval_frequency: number;
  is_recurring: boolean;
  is_active: boolean;
  activation_date: string;
  member_count: number;
  active_member_count?: number;
  pay_received: string;
  pay_due: string;
  allow_activation_date_update: boolean;
  payment_list: PaymentList[];
  is_payment_link_sent: boolean;
  is_split_payment: boolean;
  pagination: Pagination;
  next_due_at: string;
  remind_again: boolean;
  collection_started_at: string;
  created_at: string;
  start_date: any;
  end_date: any;
  is_attendance_tracking_enabled: boolean;
  is_send_attendance_alert_enabled: boolean;
  work_days: any;
  is_attendance_marked: boolean;
  is_fee_configured: boolean;
  group_fee_amount_configuration: any;
  payment_schedule?: PaymentScheduleType;
  attendance_config?: {
    is_attendance_tracking_enabled?: boolean;
    is_attendance_based_fee_enabled?: boolean;
    is_send_attendance_alert_enabled?: boolean;
    working_days?: string[];
  };
  is_attendance_based_fee_enabled?: boolean;
};

export type PaymentList = {
  payment_due: string;
  payment_received: string;
  paid_member_count: number;
  total_member_count: number;
  payment_month: string;
  payment_year: string;
};

export type Pagination = {
  page: number;
  limit: number;
  total_rows: number;
  last_page: boolean;
};

export enum MemberStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive'
}

export type AddFromMembersListItem = {
  id: string;
  name: string;
  mobile: string;
  countryCode: string;
  admissionNumber: string;
  dueAmount: string;
  dueDate: string;
  isAlreadyPresent: boolean;
  feeCategoryData: FeeCategoryDataType[];
  paymentCollectionType?: PaymentCollectionType;
  interval?: string;
  isManualChangeMade?: boolean;
  isAmountManuallyEdited: boolean;
  isManualChangeMadeForDueDate?: boolean;
  isManualChangeMadeForDueAmount?: boolean;
  isManualChangeMadeForStartDate?: boolean;
  isManualChangeMadeForSessionsPerCycle?: boolean;
  startDate?: string;
  member_id?: string;
  primary_mobile_number?: string;
  primary_country_code?: string;
  sessionsPerCycle?: number;
};

export type AddMemberModeType = 'File' | 'Manual' | 'MemberList';

export interface AmountChangeParams {
  value: string;
  index: number;
}

export type GetFeeCategoryDataFromConfig = (
  props?: FeeConfigurationType[]
) => FeeCategoryDataType[];

export type FeeCategoryInputType = {
  category: string;
  amount: string;
  isManuallyUpdated?: boolean;
};

export enum GroupFeeCollectionMethod {
  FIXED = 'fixed',
  ATTENDANCE_BASED = 'attendance-based'
}

// Types for v3 Add Member to Group API
export interface GroupMemberError {
  field: string;
  message: string;
}

export interface GroupMemberResponseItem {
  name: string;
  mobile: string;
  country_code: string;
  alternate_mobile?: string;
  secondary_country_code?: string;
  email?: string;
  admission_number?: string;
  admission_date?: string;
  date_of_birth?: string;
  guardian_name?: string;
  age?: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  amount?: string;
  payment_schedule?: {
    interval?: string;
    interval_frequency?: number;
    sessions_per_cycle?: number;
    monthly_config?: {
      day_option?: string;
      custom_day?: number;
    };
  };
  custom_fields?: Array<{ key: string; value: string }>;
  errors: GroupMemberError[] | null;
}

export interface GroupAddMemberResponseData {
  members_added: {
    members: GroupMemberResponseItem[];
    warnings: GroupMemberResponseItem[] | null;
    success_records: GroupMemberResponseItem[] | null;
  };
}

export interface GroupAddMemberResponse {
  status: string;
  data: GroupAddMemberResponseData;
}

export interface AddMemberToGroupRequestBody {
  members_added: any[];
  force_create: boolean;
}

export type EligibleMember = {
  member_id: string;
  group_member_id: number;
  name: string;
  amount: string;
  next_due_at: string;
  status: string;
  is_default_member: boolean;
  is_payment_link_sent: boolean;
  term_details?: TermsConfig[];
};

export type IneligibleMember = {
  member_id: string;
  group_member_id: number;
  name: string;
  amount: string;
  next_due_at: string;
  status: string;
  reason: string;
};

export type UpdateEligibleMembersResponse = {
  eligible_members: EligibleMember[];
  ineligible_members: IneligibleMember[];
};

export type CustomMemberUpdate = {
  member_id: string;
  skip_current_interval: boolean;
  default_member?: boolean;
};
